import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useCart } from '../contexts/CartContext';
import apiService, { toThumbSrc, toImageSrc } from '../services/api';
import analytics from '../services/analytics';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';

const CheckoutWrapper = styled.div`
  padding: var(--spacing-8) 0;
  min-height: 60vh;
`;

const Title = styled.h1`
  font-size: var(--font-size-3xl);
  font-weight: 600;
  margin-bottom: var(--spacing-6);
  color: var(--color-secondary);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: var(--spacing-8);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-6);
`;

const Field = styled.div`
  margin-bottom: var(--spacing-4);
  label { display: block; font-weight: 600; margin-bottom: var(--spacing-2); }
  input, textarea { width: 100%; padding: var(--spacing-3); border: 1px solid var(--color-border); border-radius: var(--border-radius); }
  textarea { resize: vertical; }
`;

const Button = styled.button`
  background-color: var(--color-secondary);
  color: white;
  padding: var(--spacing-4) var(--spacing-6);
  border: none;
  border-radius: var(--border-radius);
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const SummaryRow = styled.div<{ total?: boolean }>`
  display: flex; justify-content: space-between; margin-bottom: var(--spacing-2);
  font-weight: ${({ total }) => (total ? 700 : 400)};
`;

const ErrorText = styled.div`
  color: var(--color-error);
  margin: var(--spacing-3) 0;
  padding: var(--spacing-3);
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: var(--border-radius);
`;

const ErrorField = styled.input<{ hasError?: boolean }>`
  border: ${({ hasError }) => hasError ? '2px solid var(--color-error)' : '1px solid var(--color-border)'} !important;
  background-color: ${({ hasError }) => hasError ? '#fef2f2' : 'white'} !important;
`;

const CartItems = styled.div`
  margin-bottom: var(--spacing-6);
  border-bottom: 1px solid var(--color-border);
  padding-bottom: var(--spacing-4);
`;

const CartItem = styled.div`
  display: flex;
  align-items: center;
  padding: var(--spacing-3) 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemImage = styled.div`
  width: 60px;
  height: 60px;
  border-radius: var(--border-radius);
  overflow: hidden;
  background-color: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: var(--spacing-3);
  flex-shrink: 0;
  padding: 6px;
  box-sizing: border-box;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  
  span {
    font-size: 24px;
  }
`;

const ItemDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.div`
  font-weight: 500;
  font-size: var(--font-size-sm);
  color: var(--color-text);
  margin-bottom: 2px;
`;

const ItemPrice = styled.div`
  font-size: var(--font-size-sm);
  color: var(--color-text-light);
`;

const ItemQuantity = styled.div`
  font-size: var(--font-size-sm);
  color: var(--color-text-light);
  margin-left: var(--spacing-2);
  font-weight: 500;
`;

function formatSEK(amount: number) {
  return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
}

function getProductEmoji(categories?: string[]): string {
  if (!categories || categories.length === 0) return '🏺';
  if (categories.some(cat => cat.toLowerCase().includes('växt') || cat.toLowerCase().includes('planta') || cat.toLowerCase().includes('kruka'))) {
    return '🌱';
  }
  if (categories.some(cat => cat.toLowerCase().includes('ljus') || cat.toLowerCase().includes('doft'))) {
    return '🕯️';
  }
  if (categories.some(cat => cat.toLowerCase().includes('dekoration') || cat.toLowerCase().includes('inredning'))) {
    return '🏡';
  }
  if (categories.some(cat => cat.toLowerCase().includes('presentkort'))) {
    return '🎁';
  }
  return '🌸';
}

const PaymentStep: React.FC<{ 
  onSuccess: () => void; 
  clientSecret: string;
}> = ({ onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError('');
    try {
      const { error: stripeError } = await stripe.confirmPayment({ 
        elements, 
        redirect: 'if_required',
        confirmParams: {
          return_url: window.location.origin + '/tack'
        }
      });
      if (stripeError) {
        setError(stripeError.message || 'Betalningen misslyckades. Försök igen.');
      } else {
        onSuccess();
      }
    } catch (err: any) {
      setError(err?.message || 'Något gick fel vid betalning.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handlePay}>
      <PaymentElement 
        options={{ 
          layout: {
            type: 'tabs',
            defaultCollapsed: false
          },
          fields: {
            billingDetails: 'auto'
          }
        }} 
      />
      {error && <ErrorText>{error}</ErrorText>}
      <div style={{ marginTop: 'var(--spacing-4)' }}>
        <Button type="submit" disabled={!stripe || submitting}>
          {submitting ? 'Bearbetar...' : 'Betala'}
        </Button>
      </div>
    </form>
  );
};

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { items, totalItems, clearCart, cartCalculation, isCalculating, calculateSecureCart, currentCampaignCode, setCurrentCampaignCode } = useCart();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [shippingMethod, setShippingMethod] = useState<'delivery' | 'pickup'>('delivery');
  const [creating, setCreating] = useState(false);
  const [clientSecret, setClientSecret] = useState<string>('');
  const [publishableKey, setPublishableKey] = useState<string>('');
  const [orderId, setOrderId] = useState<number | null>(null);
  const [orderData, setOrderData] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: boolean}>({});
  const [campaignCodeDiscount, setCampaignCodeDiscount] = useState<number>(0);
  const [campaignCodeMessage, setCampaignCodeMessage] = useState<string>('');
  const [validatingCode, setValidatingCode] = useState<boolean>(false);
  const [campaignValidated, setCampaignValidated] = useState<boolean>(false);

  // Use secure backend calculations ONLY - no fallback to client-side prices
  const secureTotal = cartCalculation?.total_amount || 0;
  const secureTaxAmount = cartCalculation?.tax_amount || 0;

  // FORCE BUILD CHANGE v1.1
  const secureShippingCost = cartCalculation?.shipping_cost || 0;
  const secureCampaignDiscount = cartCalculation?.campaign_discount_amount || 0;

  const backendCampaignPercentage = cartCalculation?.campaign_discount || 0;
  const backendCampaignAmount = cartCalculation?.campaign_discount_amount || 0;
  const backendCampaignActive = backendCampaignPercentage > 0 || backendCampaignAmount > 0;

  const effectiveCampaignMessage = backendCampaignActive
    ? (backendCampaignPercentage > 0
        ? `Rabattkod tillämpad: ${backendCampaignPercentage}% rabatt`
        : 'Rabattkod tillämpad')
    : campaignCodeMessage;

  const isCampaignSuccess = backendCampaignActive || (campaignCodeDiscount > 0 && campaignValidated);

  useEffect(() => {
    apiService.getStripeConfig()
      .then(cfg => setPublishableKey(cfg.publishable_key))
      .catch(() => setError('Kunde inte hämta Stripe-konfiguration.'));
  }, []);

  const stripePromise = useMemo(() => publishableKey ? loadStripe(publishableKey) : null, [publishableKey]);

  // Recalculate when shipping method changes
  useEffect(() => {
    if (items.length > 0) {
      calculateSecureCart(shippingMethod, currentCampaignCode || undefined);
      // Don't let recalculation clear campaign validation message
    }
  }, [shippingMethod]);

  // Preserve campaign message when cart updates
  useEffect(() => {
    if (campaignValidated && currentCampaignCode && campaignCodeMessage && !campaignCodeMessage.includes('kunde inte')) {
      // Message is valid, preserve it even if cart recalculates
      const preservedMessage = campaignCodeMessage;
      const preservedDiscount = campaignCodeDiscount;

      // Small delay to ensure any competing state updates have finished
      const timeout = setTimeout(() => {
        if (!campaignCodeMessage || campaignCodeMessage.includes('kunde inte')) {
          setCampaignCodeMessage(preservedMessage);
          setCampaignCodeDiscount(preservedDiscount);
        }
      }, 100);

      return () => clearTimeout(timeout);
    }
  }, [cartCalculation, campaignValidated, currentCampaignCode]);

  // Keep campaign validation state aligned with backend totals
  useEffect(() => {
    if (!currentCampaignCode || !backendCampaignActive) {
      return;
    }

    const normalizedDiscount = Number(backendCampaignPercentage || 0);
    const appliedMessage = normalizedDiscount > 0
      ? `Rabattkod tillämpad: ${normalizedDiscount}% rabatt`
      : 'Rabattkod tillämpad';

    if (
      campaignCodeDiscount !== normalizedDiscount ||
      !campaignValidated ||
      !campaignCodeMessage ||
      campaignCodeMessage.includes('kunde inte')
    ) {
      setCampaignCodeDiscount(normalizedDiscount);
      setCampaignCodeMessage(appliedMessage);
      setCampaignValidated(true);
    }
  }, [backendCampaignActive, backendCampaignPercentage, currentCampaignCode, campaignCodeDiscount, campaignCodeMessage, campaignValidated]);

  const validateCampaignCode = async (code: string) => {
    const normalizedCode = code.trim().toUpperCase();

    if (!normalizedCode) {
      setCampaignCodeDiscount(0);
      setCampaignCodeMessage('');
      setCurrentCampaignCode(null);
      setCampaignValidated(false);
      await calculateSecureCart(shippingMethod);
      return;
    }

    if (!items || items.length === 0) {
      setCampaignCodeDiscount(0);
      setCampaignCodeMessage('Kundvagnen är tom');
      setCampaignValidated(false);
      return;
    }

    setValidatingCode(true);

    try {
      setCurrentCampaignCode(normalizedCode);
      const calculation = await calculateSecureCart(shippingMethod, normalizedCode);

      if (!calculation) {
        setCampaignCodeDiscount(0);
        setCampaignCodeMessage('Kunde inte validera rabattkod');
        setCurrentCampaignCode(null);
        setCampaignValidated(false);
        await calculateSecureCart(shippingMethod);
        return;
      }

      const discountPercentage = Number(calculation.campaign_discount || 0);
      const discountAmount = Number(calculation.campaign_discount_amount || 0);

      if (discountPercentage > 0 || discountAmount > 0) {
        const appliedMessage = discountPercentage > 0
          ? `Rabattkod tillämpad: ${discountPercentage}% rabatt`
          : 'Rabattkod tillämpad';
        setCampaignCodeDiscount(discountPercentage);
        setCampaignCodeMessage(appliedMessage);
        setCampaignValidated(true);
      } else {
        setCampaignCodeDiscount(0);
        setCampaignCodeMessage('Rabattkoden kunde inte tillämpas');
        setCampaignValidated(false);
        setCurrentCampaignCode(null);
        await calculateSecureCart(shippingMethod);
      }
    } catch (error: any) {
      console.error('Campaign validation error:', error);
      setCampaignCodeDiscount(0);
      setCampaignCodeMessage('Rabattkoden kunde inte tillämpas');
      setCurrentCampaignCode(null);
      setCampaignValidated(false);
      await calculateSecureCart(shippingMethod);
    } finally {
      setValidatingCode(false);
    }
  };

  const handleCampaignCodeChange = (code: string) => {
    setCurrentCampaignCode(code);
    if (!code.trim()) {
      setCampaignCodeDiscount(0);
      setCampaignCodeMessage('');
      setCampaignValidated(false);
    }
  };

  const applyCampaignCode = () => {
    validateCampaignCode(currentCampaignCode || '');
  };

  const handleCreateAndInit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all required fields
    const requiredFields = {
      email: { value: email, label: 'E-post' },
      firstName: { value: firstName, label: 'Förnamn' },
      lastName: { value: lastName, label: 'Efternamn' },
      phone: { value: phone, label: 'Telefon' },
      ...(shippingMethod === 'delivery' ? {
        address: { value: address, label: 'Adress' },
        postalCode: { value: postalCode, label: 'Postnummer' },
        city: { value: city, label: 'Stad' }
      } : {})
    };

    const errors: {[key: string]: boolean} = {};
    const missingFields: string[] = [];

    Object.entries(requiredFields).forEach(([key, field]) => {
      if (!field.value || field.value.trim() === '') {
        errors[key] = true;
        missingFields.push(field.label);
      }
    });

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && !emailRegex.test(email)) {
      errors.email = true;
      if (!missingFields.includes('E-post')) {
        missingFields.push('E-post (ogiltigt format)');
      }
    }

    // Validate postal code format (Swedish format: 12345 or 123 45) - only for delivery
    if (shippingMethod === 'delivery') {
      const postalRegex = /^\d{3}\s?\d{2}$/;
      if (postalCode && !postalRegex.test(postalCode)) {
        errors.postalCode = true;
        if (!missingFields.includes('Postnummer')) {
          missingFields.push('Postnummer (ogiltigt format)');
        }
      }
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setError(`Följande fält måste fyllas i korrekt: ${missingFields.join(', ')}`);
      return;
    }

    setCreating(true);
    setError('');
    setFieldErrors({});
    
    try {
      // Always fetch the latest secure calculation to avoid stale totals
      const latestCalculation = await calculateSecureCart(shippingMethod, currentCampaignCode || undefined);
      const effectiveCalculation = latestCalculation || cartCalculation;

      if (!effectiveCalculation) {
        setError('Kunde inte beräkna totalsumman. Försök igen.');
        setCreating(false);
        return;
      }

      const effectiveTotal = Number((effectiveCalculation.total_amount ?? 0).toFixed(2));
      const effectiveTax = Number((effectiveCalculation.tax_amount ?? 0).toFixed(2));
      const effectiveShipping = Number((effectiveCalculation.shipping_cost ?? 0).toFixed(2));
      const effectiveCampaignPercentage = effectiveCalculation.campaign_discount ?? 0;
      const effectiveCampaignAmount = Number((effectiveCalculation.campaign_discount_amount ?? 0).toFixed(2));

      if (effectiveTotal <= 0) {
        setError('Kontrollera totalsumman.');
        setCreating(false);
        return;
      }

      const normalizedAddress = address.trim();
      const normalizedCity = city.trim();
      const normalizedPostalCode = postalCode.replace(/\s+/g, '').trim();
      const fullAddress = shippingMethod === 'pickup'
        ? 'Hämtas ut i butik'
        : `${normalizedAddress}, ${normalizedPostalCode} ${normalizedCity}`;

      const shippingDetails = shippingMethod === 'delivery' && normalizedAddress && normalizedCity && normalizedPostalCode ? {
        line1: normalizedAddress,
        city: normalizedCity,
        postal_code: normalizedPostalCode,
        country: 'SE'
      } : null;
      const orderPayload = {
        order_number: `ORD-${Date.now()}`,
        email,
        first_name: firstName,
        last_name: lastName,
        phone,
        billing_address: fullAddress,
        shipping_address: fullAddress,
        total_amount: effectiveTotal,
        shipping_cost: effectiveShipping,
        tax_amount: effectiveTax,
        status: 'pending',
        payment_method: 'card',
        payment_status: 'pending',
        notes: shippingMethod === 'pickup' ? 'Hämtas ut i butik' : '',
        campaign_code: currentCampaignCode || null,
        campaign_discount_percentage: effectiveCampaignPercentage,
        campaign_discount_amount: effectiveCampaignAmount,
        items: (latestCalculation?.items || cartCalculation?.items || items).map(item => ({
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
          unit_price: item.final_price,
          total_price: item.item_total
        }))
      };

      // Store order data for later creation after successful payment
      setOrderData(orderPayload);

      // GA4: begin_checkout event using secure backend totals
      analytics.trackBeginCheckout(orderPayload);

      const intent = await apiService.createPaymentIntent({
        total_amount: effectiveTotal,
        email,
        first_name: firstName,
        last_name: lastName,
        shipping_address: fullAddress,
        shipping_details: shippingDetails,
        shipping_method: shippingMethod,
        order_data: orderPayload // Pass order data to be stored in payment metadata
      });

      setClientSecret(intent.client_secret);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || 'Kunde inte initiera betalningen.');
    } finally {
      setCreating(false);
    }
  };

  const handlePaid = async () => {
    // Order will be created by Stripe webhook upon payment success
    // If campaign code was used, increment its usage counter
    if (currentCampaignCode && campaignCodeDiscount > 0) {
      try {
        // The backend should handle incrementing usage when order is created via webhook
        // But we can also do it here as a backup to ensure usage is tracked
        await apiService.post('/campaign-codes/validate', { code: currentCampaignCode });
      } catch (error) {
        console.error('Failed to increment campaign code usage:', error);
      }
    }

    // GA4: purchase event after successful payment
    if (orderData) {
      analytics.trackPurchase({
        order_number: orderData.order_number,
        total_amount: orderData.total_amount,
        shipping_cost: orderData.shipping_cost,
        tax_amount: orderData.tax_amount,
        campaign_code: orderData.campaign_code || null,
        items: (orderData.items || []).map((item: any) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.unit_price,
          name: item.product_name
        }))
      });
    }
    
    // Navigate to thank you page and clear cart
    clearCart();
    navigate('/tack');
  };

  if (items.length === 0 && !success) {
    return (
      <CheckoutWrapper>
        <div className="container">
          <Title>Kassa</Title>
          <Card style={{ textAlign: 'center' }}>
            <p>Ingen beställning att genomföra.</p>
            <Link to="/produkter" className="btn btn-primary">Fortsätt handla</Link>
          </Card>
        </div>
      </CheckoutWrapper>
    );
  }

  return (
    <CheckoutWrapper>
      <div className="container">
        <Title>Kassa</Title>
        <Grid>
          <div>
            {!clientSecret && !success && (
              <Card>
                <h3 style={{ marginTop: 0 }}>Kunduppgifter</h3>
                <form onSubmit={handleCreateAndInit}>
                  <Field>
                    <label>E-post *</label>
                    <ErrorField 
                      type="email" 
                      value={email} 
                      onChange={(e) => setEmail(e.target.value)} 
                      placeholder="din@email.com"
                      hasError={fieldErrors.email}
                      required 
                    />
                  </Field>
                  <Field>
                    <label>Förnamn *</label>
                    <ErrorField 
                      type="text" 
                      value={firstName} 
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Ditt förnamn"
                      hasError={fieldErrors.firstName} 
                      required 
                    />
                  </Field>
                  <Field>
                    <label>Efternamn *</label>
                    <ErrorField 
                      type="text" 
                      value={lastName} 
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Ditt efternamn"
                      hasError={fieldErrors.lastName}
                      required 
                    />
                  </Field>
                  <Field>
                    <label>Telefon *</label>
                    <ErrorField 
                      type="tel" 
                      value={phone} 
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="070-123 45 67"
                      hasError={fieldErrors.phone}
                      required 
                    />
                  </Field>
                  <Field>
                    <label>Adress *</label>
                    <ErrorField 
                      type="text" 
                      value={address} 
                      onChange={(e) => setAddress(e.target.value)} 
                      placeholder="Storgatan 123"
                      hasError={fieldErrors.address}
                      required 
                    />
                  </Field>
                  <Field>
                    <label>Postnummer *</label>
                    <ErrorField 
                      type="text" 
                      value={postalCode} 
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                        if (value.length > 3) {
                          value = value.slice(0, 3) + ' ' + value.slice(3, 5); // Format as "123 45"
                        }
                        setPostalCode(value);
                      }} 
                      placeholder="123 45" 
                      maxLength={6}
                      hasError={fieldErrors.postalCode}
                      required 
                    />
                  </Field>
                  <Field>
                    <label>Stad *</label>
                    <ErrorField 
                      type="text" 
                      value={city} 
                      onChange={(e) => setCity(e.target.value)} 
                      placeholder="Stockholm"
                      hasError={fieldErrors.city}
                      required 
                    />
                  </Field>
                  <Field>
                    <label style={{ marginBottom: '16px', display: 'block', fontSize: '16px', fontWeight: '600' }}>Leveransalternativ *</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div 
                        onClick={() => setShippingMethod('delivery')}
                        style={{ 
                          position: 'relative',
                          cursor: 'pointer',
                          padding: '20px',
                          border: shippingMethod === 'delivery' ? '2px solid #4CAF50' : '1px solid #e5e7eb',
                          borderRadius: '12px',
                          backgroundColor: shippingMethod === 'delivery' ? '#f0fdf4' : '#ffffff',
                          boxShadow: shippingMethod === 'delivery' 
                            ? '0 4px 12px rgba(76, 175, 80, 0.15)' 
                            : '0 1px 3px rgba(0, 0, 0, 0.1)',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <input 
                          type="radio" 
                          name="shippingMethod" 
                          value="delivery" 
                          checked={shippingMethod === 'delivery'} 
                          onChange={() => setShippingMethod('delivery')}
                          style={{ 
                            position: 'absolute',
                            top: '20px',
                            right: '20px',
                            width: '20px',
                            height: '20px',
                            accentColor: '#4CAF50'
                          }}
                        />
                        <div style={{ marginRight: '40px' }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            marginBottom: '8px',
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#1f2937'
                          }}>
                            <span style={{ 
                              fontSize: '24px', 
                              marginRight: '12px',
                              display: 'inline-block',
                              width: '32px',
                              textAlign: 'center'
                            }}>📦</span>
                            Frakt
                          </div>
                          <p style={{ 
                            margin: 0,
                            color: '#6b7280', 
                            fontSize: '14px',
                            lineHeight: '1.5',
                            paddingLeft: '44px'
                          }}>
                            Frakt 59 SEK inom Sverige.
                          </p>
                          {shippingMethod === 'delivery' && (
                            <div style={{
                              marginTop: '12px',
                              paddingLeft: '44px',
                              fontSize: '14px',
                              fontWeight: '500',
                              color: '#059669'
                            }}>
                              ✓ Valt
                            </div>
                          )}
                        </div>
                      </div>

                      <div 
                        onClick={() => setShippingMethod('pickup')}
                        style={{ 
                          position: 'relative',
                          cursor: 'pointer',
                          padding: '20px',
                          border: shippingMethod === 'pickup' ? '2px solid #4CAF50' : '1px solid #e5e7eb',
                          borderRadius: '12px',
                          backgroundColor: shippingMethod === 'pickup' ? '#f0fdf4' : '#ffffff',
                          boxShadow: shippingMethod === 'pickup' 
                            ? '0 4px 12px rgba(76, 175, 80, 0.15)' 
                            : '0 1px 3px rgba(0, 0, 0, 0.1)',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <input 
                          type="radio" 
                          name="shippingMethod" 
                          value="pickup" 
                          checked={shippingMethod === 'pickup'} 
                          onChange={() => setShippingMethod('pickup')}
                          style={{ 
                            position: 'absolute',
                            top: '20px',
                            right: '20px',
                            width: '20px',
                            height: '20px',
                            accentColor: '#4CAF50'
                          }}
                        />
                        <div style={{ marginRight: '40px' }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            marginBottom: '8px',
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#1f2937'
                          }}>
                            <span style={{ 
                              fontSize: '24px', 
                              marginRight: '12px',
                              display: 'inline-block',
                              width: '32px',
                              textAlign: 'center'
                            }}>🏪</span>
                            Hämta ut i butik
                            <span style={{ 
                              marginLeft: '12px',
                              backgroundColor: '#10b981', 
                              color: 'white', 
                              padding: '4px 8px', 
                              borderRadius: '6px', 
                              fontSize: '12px',
                              fontWeight: '700',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>Gratis</span>
                          </div>
                          <p style={{ 
                            margin: 0,
                            color: '#6b7280', 
                            fontSize: '14px',
                            lineHeight: '1.5',
                            paddingLeft: '44px'
                          }}>
                            Hämta din beställning direkt i vår butik utan kostnad
                          </p>
                          {shippingMethod === 'pickup' && (
                            <div style={{
                              marginTop: '12px',
                              paddingLeft: '44px',
                              fontSize: '14px',
                              fontWeight: '500',
                              color: '#059669'
                            }}>
                              ✓ Valt
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Field>

                  <Field>
                    <label>Har du en rabattkod?</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <input
                          type="text"
                          value={currentCampaignCode || ''}
                          onChange={(e) => handleCampaignCodeChange(e.target.value.toUpperCase())}
                          placeholder="Ange din rabattkod"
                          style={{ margin: 0 }}
                        />
                        {effectiveCampaignMessage && (
                          <div
                            style={{
                              marginTop: '4px',
                              fontSize: '14px',
                              color: isCampaignSuccess ? '#16a34a' : '#dc2626',
                              fontWeight: isCampaignSuccess ? '500' : '400'
                            }}
                          >
                            {effectiveCampaignMessage}
                          </div>
                        )}
                      </div>
                      <Button
                        type="button"
                        onClick={applyCampaignCode}
                        disabled={!currentCampaignCode?.trim() || validatingCode}
                        style={{
                          padding: '12px 16px',
                          fontSize: '14px',
                          backgroundColor: isCampaignSuccess ? '#16a34a' : 'var(--color-secondary)'
                        }}
                      >
                        {validatingCode ? 'Validerar...' : isCampaignSuccess ? '✓ Tillämpad' : 'Tillämpa'}
                      </Button>
                    </div>
                  </Field>

                  {error && <ErrorText>{error}</ErrorText>}
                  <Button type="submit" disabled={creating || !publishableKey}>
                    {creating ? 'Skapar betalning...' : 'Fortsätt till betalning'}
                  </Button>
                </form>
              </Card>
            )}

            {clientSecret && !success && publishableKey && (
              <Card>
                <h3 style={{ marginTop: 0 }}>Betalning</h3>
                <Elements
                  stripe={stripePromise as any}
                  options={{ 
                    clientSecret, 
                    appearance: { 
                      theme: 'flat',
                      variables: {
                        colorPrimary: 'var(--color-secondary)',
                        colorBackground: '#ffffff',
                        colorText: '#333333',
                        fontFamily: 'system-ui, sans-serif'
                      }
                    },
                    locale: 'sv' as const
                  } as StripeElementsOptions}
                >
                  <PaymentStep 
                    onSuccess={handlePaid} 
                    clientSecret={clientSecret}
                  />
                </Elements>
              </Card>
            )}

            {success && (
              <Card>
                <h3 style={{ marginTop: 0 }}>Omdirigerar...</h3>
                <p>Du omdirigeras till bekräftelsesidan...</p>
              </Card>
            )}
          </div>

          <div>
            <Card>
              <h3 style={{ marginTop: 0 }}>Ordersammanfattning</h3>
              
              <CartItems>
                {items.map((item, index) => (
                  <CartItem key={`${item.id}-${index}`}>
                    <ItemImage>
                      {item.images && item.images.length > 0 ? (
                        <img
                          src={toThumbSrc(item.images[0])}
                          alt={item.name}
                          onError={(e) => {
                            const target = e.currentTarget as HTMLImageElement;
                            target.onerror = null;
                            target.src = toImageSrc(item.images![0]);
                          }}
                        />
                      ) : (
                        <span>{getProductEmoji(item.categories)}</span>
                      )}
                    </ItemImage>
                    <ItemDetails>
                      <ItemName>
                        {item.name}
                        {item.has_discount && (
                          <span style={{
                            backgroundColor: '#dc2626',
                            color: 'white',
                            fontSize: '0.7em',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            marginLeft: '8px',
                            fontWeight: '600'
                          }}>
                            RABATT
                          </span>
                        )}
                      </ItemName>
                      <div>
                        {item.has_discount ? (
                          <div>
                            <span style={{
                              textDecoration: 'line-through',
                              color: '#9ca3af',
                              fontSize: '0.9em',
                              marginRight: '8px'
                            }}>
                              {formatSEK(item.original_price)}
                            </span>
                            <ItemPrice style={{ color: '#dc2626', fontWeight: '600' }}>
                              {formatSEK(item.final_price)}
                            </ItemPrice>
                          </div>
                        ) : (
                          <ItemPrice>{formatSEK(item.final_price)}</ItemPrice>
                        )}
                      </div>
                    </ItemDetails>
                    <ItemQuantity>{item.quantity} st</ItemQuantity>
                  </CartItem>
                ))}
              </CartItems>

              <SummaryRow>
                <span>Delsumma ({totalItems} st)</span>
                <span>
                  {isCalculating ? 'Beräknar...' : formatSEK(cartCalculation?.subtotal || 0)}
                </span>
              </SummaryRow>
              {!!(cartCalculation?.campaign_discount_amount && cartCalculation.campaign_discount_amount > 0) && (
                <SummaryRow>
                  <span style={{ color: '#16a34a' }}>
                    Rabattkod ({cartCalculation.campaign_discount}%)
                    {!!(cartCalculation.breakdown && cartCalculation.breakdown.discounted_products_total > 0) && (
                      <div style={{ fontSize: '0.85em', opacity: 0.8, marginTop: '2px' }}>
                        Tillämpas på {formatSEK(cartCalculation.breakdown.campaign_applied_to)}
                      </div>
                    )}
                  </span>
                  <span style={{ color: '#16a34a' }}>-{formatSEK(cartCalculation.campaign_discount_amount)}</span>
                </SummaryRow>
              )}
              <SummaryRow>
                <span>{shippingMethod === 'pickup' ? 'Hämta ut i butik' : 'Frakt'}</span>
                <span>
                  {isCalculating ? 'Beräknar...' : (secureShippingCost === 0 ? (shippingMethod === 'pickup' ? '0 SEK' : 'Gratis') : formatSEK(secureShippingCost))}
                </span>
              </SummaryRow>
              <SummaryRow>
                <span>Moms</span>
                <span>{isCalculating ? 'Beräknar...' : formatSEK(secureTaxAmount)}</span>
              </SummaryRow>
              <SummaryRow total>
                <span>Totalt</span>
                <span>{isCalculating ? 'Beräknar...' : formatSEK(secureTotal)}</span>
              </SummaryRow>

              {!clientSecret && !success && (
                <div style={{ marginTop: 'var(--spacing-4)' }}>
                  <Link to="/kundvagn" style={{ color: 'var(--color-secondary)', textDecoration: 'none' }}>Tillbaka till kundvagn</Link>
                </div>
              )}
            </Card>
          </div>
        </Grid>
      </div>
    </CheckoutWrapper>
  );
};

export default Checkout;
